import { useEffect, useRef } from 'react'
import { isCreationNode } from '../../lib/vcMenuHelpers.js'
import { S } from './vcStyles.js'

// GraphNodeMenu — right-click context menu for nodes in the version-control
// graph (PlayCanvas parity). Shows: Set as Compare Base, New Branch, Restore,
// Hard Reset, Copy Data. Items are disabled when not applicable (e.g. Restore
// on the synthetic uncheckpointed creation node). Closes on outside click or Esc.
export default function GraphNodeMenu({
  node,
  x,
  y,
  isCompareBase,
  onSetCompareBase,
  onNewBranch,
  onRestore,
  onHardReset,
  onCompareWithBase,
  onCopyData,
  onClose,
}) {
  const ref = useRef(null)
  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        // One click dismisses ONE layer: consume the event so the layer
        // underneath (e.g. the Item History modal's click-outside-to-close
        // backdrop) doesn't ALSO dismiss on the same mousedown — an outside
        // click with the menu open used to close the menu AND the whole
        // modal at once.
        e.stopPropagation()
        onClose()
      }
    }
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    // CAPTURE phase: the graph modals call stopPropagation() on mousedown (to
    // keep the overlay's click-to-close from firing), which kills bubble-phase
    // listeners — outside-clicks inside those modals never closed the menu.
    window.addEventListener('mousedown', onDown, true)
    window.addEventListener('keydown', onKey, true)
    return () => {
      window.removeEventListener('mousedown', onDown, true)
      window.removeEventListener('keydown', onKey, true)
    }
  }, [onClose])

  // The synthetic uncheckpointed creation node (id all-zeros or uncheckpointed flag)
  // cannot be restored, hard-reset, branched, or compared
  const isCreation = isCreationNode(node)

  // Build menu items in order
  const items = [
    {
      label: isCompareBase ? 'Clear Compare Base' : 'Set as Compare Base',
      // onSetCompareBase toggles (set when unset, clear when this node is the
      // base) — always call it so "Clear" actually clears.
      onClick: () => { onClose(); onSetCompareBase?.() },
      disabled: isCreation,
    },
    {
      label: 'New Branch',
      onClick: () => { onClose(); onNewBranch?.() },
      disabled: isCreation,
    },
    { separator: true },
    {
      label: 'Restore',
      onClick: () => { onClose(); onRestore?.() },
      disabled: isCreation,
    },
    {
      label: 'Hard Reset',
      onClick: () => { onClose(); onHardReset?.() },
      disabled: isCreation,
    },
    { separator: true },
    {
      label: onCompareWithBase ? 'Compare with Base' : 'Copy Data',
      onClick: () => {
        onClose()
        if (onCompareWithBase) {
          onCompareWithBase?.()
        } else {
          onCopyData?.()
        }
      },
      disabled: false,
    },
  ]

  const visible = items.filter((it) => !it.hidden)
  // keep the menu on-screen: nudge left/up if near the right/bottom edge
  const left = Math.min(x, window.innerWidth - 200)
  const top = Math.min(y, window.innerHeight - (visible.length * 30 + 16))

  return (
    <div ref={ref} style={{ ...S.menu, left, top }} role="menu">
      {visible.map((it, i) =>
        it.separator ? (
          <div key={i} style={S.menuSep} />
        ) : (
          <div
            key={i}
            role="menuitem"
            aria-disabled={it.disabled ? 'true' : 'false'}
            style={{
              ...S.menuItem,
              ...(it.disabled ? { opacity: 0.4, cursor: 'default' } : {}),
            }}
            onMouseEnter={(e) => {
              if (!it.disabled) e.currentTarget.style.background = '#1a2030'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent'
            }}
            onClick={() => { if (!it.disabled) it.onClick?.() }}
          >
            {it.label}
          </div>
        ),
      )}
    </div>
  )
}
